import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosResponse, type AxiosRequestConfig } from 'axios';
import { noteImpersonationEnded, refreshSessionCookie, refreshSessionOutcome, useAuthStore } from '@/store/authStore';
import {
  IMPERSONATION_ENDED_HEADER,
  impersonationEndedUnsyncedError,
  testModeErrorFor,
} from '@/lib/testModeErrors';
import { API_URL } from '@/lib/apiUrl';
import { getCsrfToken, isMutatingMethod } from '@/lib/csrf';

if (!API_URL) {
  console.warn('[api/client] VITE_API_URL is not set. API calls will fail.');
}

let isRefreshing = false;
// Requests that 401'd while a refresh was already running wait for its result — and must hear
// about a FAILED one too, or they hang forever now that a failure no longer always redirects.
let refreshSubscribers: Array<(ok: boolean) => void> = [];

function subscribeTokenRefresh(cb: (ok: boolean) => void) {
  refreshSubscribers.push(cb);
}

function onRefreshed(ok: boolean) {
  refreshSubscribers.forEach((cb) => cb(ok));
  refreshSubscribers = [];
}

/**
 * Rotate the session cookie using the httpOnly refresh cookie. Exported so non-axios
 * callers (the raw-fetch upload helpers, which bypass the response interceptor below)
 * can refresh + retry on a 401 too — otherwise an expired 15-minute access token fails
 * an upload with "expired token". Returns true on success.
 */
export function refreshSession(): Promise<boolean> {
  // The one shared, in-flight refresh — see store/authStore.ts refreshSessionCookie for why two
  // concurrent refreshes sign the tab out.
  return refreshSessionCookie();
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  // Send the httpOnly session/refresh cookies on every request. Cookie auth (Wave 2.4)
  // replaces the old Authorization: Bearer header — there is no JS-readable token.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — attach tenant slug + CSRF double-submit token.
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { tenantSlug, activeSiteId, impersonating } = useAuthStore.getState();
    if (tenantSlug && config.headers && !config.headers['X-Tenant-Slug']) {
      config.headers['X-Tenant-Slug'] = tenantSlug;
    }
    // Multi-site: tell the gateway which site is being edited. Absent → gateway uses the
    // tenant's primary site, so single-site behavior is unchanged.
    if (activeSiteId && config.headers && !config.headers['X-Site-Id']) {
      config.headers['X-Site-Id'] = activeSiteId;
    }
    // Double-submit CSRF on mutations: echo the JS-readable CSRF cookie into a header
    // the gateway compares against the cookie. Select impersonation vs session token.
    //
    // `/admin`, `/root` and `/auth` always take the SESSION token, even mid-impersonation:
    //   • `/admin/*` and `/root/*` — the gateway ignores the impersonation cookie there and
    //     authenticates the admin by their own session, so `requireCsrf` compares against the
    //     session CSRF cookie. Sending the impersonation token failed every admin mutation made
    //     during test mode — including `POST /admin/impersonate/stop`, the way back out.
    //   • `/auth/*` — `/auth/logout/cookie` is not behind requireAuth; it runs `requireCsrf`
    //     directly against the session cookie. With the impersonation token, logging out during
    //     test mode 403'd, api/auth.ts swallowed it, and the httpOnly impersonation cookie
    //     survived — so the next sign-in walked straight back into somebody else's account.
    if (isMutatingMethod(config.method) && config.headers) {
      // Segment boundary, not a string prefix — `/administrators` is a tenant route.
      const path = (config.url ?? '').replace(/^https?:\/\/[^/]+/, '').split('?')[0];
      const sessionAuthed = ['/admin', '/root', '/auth'].some((p) => path === p || path.startsWith(`${p}/`));
      const csrf = getCsrfToken(impersonating && !sessionAuthed);
      if (csrf) config.headers['X-CSRF-Token'] = csrf;
    }
    // When it was sent — a 401 on it is satisfied by any refresh completed after this moment,
    // including one another tab made while this request was in flight.
    (config as InternalAxiosRequestConfig & { _sentAt?: number })._sentAt = Date.now();
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — on 401, refresh the session cookie once, then retry.
apiClient.interceptors.response.use(
  async (response: AxiosResponse) => {
    // The gateway found a DEAD impersonation cookie on this request, dropped it, and answered as
    // the admin's own session — so this payload is the ADMIN's data. Re-read identity (and tell
    // every other tab: the cookies are shared, so they are all wrong now) BEFORE handing it over.
    //
    // Awaited, not fire-and-forget: released straight away, the admin's data would render under a
    // band still reading "Test Mode as <agent>" until the re-read landed — and if the re-read hit a
    // transient failure, hydrate keeps the agent identity, so it would stay that way. If identity
    // cannot be confirmed as the admin's, the response is withheld rather than shown under the
    // wrong account.
    if (response.headers?.[IMPERSONATION_ENDED_HEADER] === '1') {
      const synced = await noteImpersonationEnded();
      if (!synced || useAuthStore.getState().impersonating) {
        const err = impersonationEndedUnsyncedError() as ReturnType<typeof impersonationEndedUnsyncedError> & { config?: unknown };
        err.config = response.config;
        return Promise.reject(err);
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((ok) => (ok ? resolve(apiClient(originalRequest)) : reject(error)));
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Rotate the session cookie via the httpOnly refresh cookie, then replay.
        const outcome = await refreshSessionOutcome(
          typeof originalRequest._sentAt === 'number' ? originalRequest._sentAt : undefined,
        );
        if (outcome === 'ok') {
          isRefreshing = false;
          onRefreshed(true);
          return apiClient(originalRequest);
        }
        isRefreshing = false;
        onRefreshed(false);
        if (outcome === 'unavailable') {
          // No verdict on the session (offline, a 5xx, another tab's rotation in flight). Signing
          // out here would sign out EVERY tab (logout broadcasts) over a blip — fail this one
          // request instead; the next one tries again.
          const err = new Error(
            'We could not renew your session just now. Check your connection and try again.',
          ) as Error & { serverCode?: string; config?: unknown };
          err.serverCode = 'session_refresh_unavailable';
          err.config = originalRequest;
          return Promise.reject(err);
        }
        // The server said the session is over — drop auth (and tell other tabs) and go to login.
        useAuthStore.getState().logout();
        if (typeof window !== 'undefined') window.location.href = '/login';
        return Promise.reject(error);
      } catch {
        isRefreshing = false;
        onRefreshed(false);
        return Promise.reject(error);
      }
    }

    // Surface the SERVER's explanation instead of axios's "Request failed with status code 500".
    // Every caller reads `e.message`, so without this the one useful sentence — "the AI provider
    // is rate-limiting us, wait a few seconds" — is replaced by a status code, and the chat
    // appends its own guess ("try rephrasing") which is often the wrong advice entirely.
    const body = error.response?.data as
      | { error?: unknown; code?: unknown; detail?: unknown; route?: unknown; message?: unknown }
      | undefined;
    // The AI limit (gateway lib/ai-budget.ts) answers { error: 'AI_LIMIT_EXCEEDED', message: '…' }:
    // the CODE in `error` and the sentence in `message`. Without this, callers that don't special-
    // case it showed a Free account the raw "AI_LIMIT_EXCEEDED" instead of what to do.
    if (
      typeof body?.error === 'string' && /^[A-Z][A-Z0-9_]+$/.test(body.error) &&
      typeof body.message === 'string' && body.message.trim()
    ) {
      if (typeof body.code !== 'string') (body as { code?: unknown }).code = body.error;
      body.error = body.message.trim();
    }
    if (typeof body?.error === 'string' && body.error.trim()) {
      // Append the cause when the server sent one. It only does so for the classes its own
      // message cannot identify — an unclassified failure, a request we built wrong, a missing
      // model — and those are exactly the ones where "The AI request failed" ended the
      // investigation, because the real reason lived only in a server log nobody watching the
      // chat (or a CI run, or a bug report) can reach.
      const detail = typeof body.detail === 'string' ? body.detail.trim() : '';
      const route = typeof body.route === 'string' ? body.route.trim() : '';
      const where = route ? ` [${route}]` : '';
      error.message = detail ? `${body.error}${where} — ${detail}` : `${body.error}${where}`;
      if (typeof body.code === 'string') (error as { serverCode?: string }).serverCode = body.code;
    }
    // The two Test Mode codes, whose raw text is worse on screen than no text at all — mapped by
    // the SAME helper the raw-fetch path uses (lib/testModeErrors testModeErrorFor), so an upload
    // or a stream says exactly what a Save does.
    //
    // `requires_direct_auth`: an admin is impersonating and this route is not open to Test Mode
    // (the gateway denies by default; services/api-gateway/src/middleware/auth.ts).
    // `impersonation_expired`: the impersonation LAPSED mid-work. The gateway refuses the write
    // rather than re-attributing it to the admin and has dropped the dead cookies, so a re-read is
    // what takes the band down.
    const testMode = testModeErrorFor(error.response?.status, body);
    if (testMode) {
      (error as { serverCode?: string }).serverCode = testMode.serverCode;
      error.message = testMode.message;
      if (testMode.serverCode === 'impersonation_expired') void noteImpersonationEnded();
    }
    return Promise.reject(error);
  }
);

// Helper to make typed API calls
export async function apiGet<T>(
  path: string,
  params?: Record<string, unknown>,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.get<{ data: T }>(path, { ...config, params });
  return response.data.data;
}

/**
 * Address ONE request to a specific site, rather than to whichever site the store happens to hold
 * when axios builds it.
 *
 * The request interceptor fills `X-Site-Id` from `useAuthStore.getState().activeSiteId` at
 * request-build time, and defers to the header when it is already set — so passing this is a pin,
 * not a fight with the interceptor.
 *
 * It exists because a cache key and a request can disagree. Keying a React Query entry by site
 * while letting the interceptor choose the site a moment later moves the disagreement one layer
 * down instead of removing it: the key says A and the response can be B's. A read that names its
 * site in both places cannot do that.
 *
 * `undefined` returns undefined, so a caller that does not know its site behaves exactly as it
 * did before — the interceptor decides, single-site behaviour is unchanged.
 */
export function siteScoped(siteId: string | null | undefined): AxiosRequestConfig | undefined {
  return siteId ? { headers: { 'X-Site-Id': siteId } } : undefined;
}

export async function apiPost<T>(path: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.post<{ data: T }>(path, body, config);
  return response.data.data;
}

export async function apiPut<T>(path: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.put<{ data: T }>(path, body, config);
  return response.data.data;
}

export async function apiPatch<T>(path: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.patch<{ data: T }>(path, body, config);
  return response.data.data;
}

export async function apiDelete<T>(path: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.delete<{ data: T }>(path, config);
  return response.data.data;
}

/**
 * Multipart file upload. Pass a FormData — axios sets the correct
 * Content-Type boundary automatically. Do NOT set Content-Type manually.
 */
export async function apiUpload<T>(
  path: string,
  formData: FormData,
  onProgress?: (pct: number) => void,
): Promise<T> {
  const response = await apiClient.post<{ data: T }>(path, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress
      ? (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)); }
      : undefined,
  });
  return response.data.data;
}
